"use client";

import { useEffect, useState, useMemo, useRef } from "react";
import DefaultStorePage from "./template/default/store-page";
import ConvetiStorePage from "./template/conveti/store-page";
import DefaultAuthPage from "./template/default/auth/auth-page";
import ConvetiAuthPage from "./template/conveti/auth/auth-page";
import DefaultProductPage from "./template/default/product/product-page";
import ConvetiProductPage from "./template/conveti/product/product-page";
import DefaultProfileContainer from "./template/default/profile/profile-container";
import ConvetiProfileContainer from "./template/conveti/profile/profile-container";
import DefaultStoreModal from "./template/default/store-modal";
import ConvetiStoreModal from "./template/conveti/store-modal";
import MarketPlace from "../market-place/marketplace";
import ModalContainer from "../modal/modal-container";
import NotFound from "../common/NotFound";
import MarketplaceCartPage from "../market-place/marketplaceCartPage";
import DefaultCartPage from "./template/default/cart/cart-page";
import ConvetiCartPage from "./template/conveti/cart/cart-page";
import {
  generateProductUrl,
  getImageUrlWithResolution,
  getStoreViewTitle,
  getLoggedinUser,
} from "../../lib/util";
import { useGetActiveCart } from "../../lib/services/collection-service";
import { getActiveUser } from "../slice/auth-slice";
import { useAppDispatch, useAppSelector } from "../slice/hooks";
import { getCartByOrgAndAccessPoint, getCartSource, setCartSource, updateCart } from "../slice/cart-slice";
import { useHistoryManager } from "../history/useHistoryManager";
import { useModalManager } from "../modal/useModalManager";
import {
  getFullPath,
  getStorePath,
  getUrlData,
  getUrlPath,
  updateFullPath,
} from "../slice/path-slice";
import {
  getAccessPoint,
  getAccessPointId,
  getAccessPoints,
  getOrganisation,
  setAccessPoint,
  setAccessPointId,
  setAccessPoints,
  setOrganisation,
} from "../slice/accessOrg-slice";
import {
  getActiveCategory,
  getCategories,
  setActiveCategory,
  setCategories,
} from "../slice/category-slice";
import { resetSiteTitle, setSiteTitle } from "../slice/site-title-slice";
import MarketPlaceStore from "../market-place/marketplaceStore";

const storeTemplates = {
  default: {
    StorePage: DefaultStorePage,
    ProductPage: DefaultProductPage,
    StoreModal: DefaultStoreModal,
    CartPage: DefaultCartPage,
    AuthPage: DefaultAuthPage,
    ProfileContainer: DefaultProfileContainer,
  },
  conveti: {
    StorePage: ConvetiStorePage,
    ProductPage: ConvetiProductPage,
    StoreModal: ConvetiStoreModal,
    CartPage: ConvetiCartPage,
    AuthPage: ConvetiAuthPage,
    ProfileContainer: ConvetiProfileContainer,
  },
};

const resolveStoreTemplateName = (data, organisation, navLocation) => {
  if (navLocation === "marketplace") return "default";

  const settings = organisation?.settings || data?.org?.organisation?.settings || {};
  const rawTemplate = settings.storeTemplate;
  const normalizedTemplate = String(rawTemplate || "default").toLowerCase();

  if (normalizedTemplate === "converti") return "conveti";
  return storeTemplates[normalizedTemplate] ? normalizedTemplate : "default";
};

const setDocumentFavicon = (href) => {
  if (typeof document === "undefined" || !href) return;

  document
    .querySelectorAll(
      'link[rel="icon"], link[rel="shortcut icon"], link[rel="apple-touch-icon"]',
    )
    .forEach((link) => link.remove());

  ["icon", "shortcut icon", "apple-touch-icon"].forEach((rel) => {
    const link = document.createElement("link");

    link.setAttribute("href", href);
    link.setAttribute("rel", rel);
    link.setAttribute("sizes", "50x50");
    document.head.appendChild(link);
  });
};

const Container = ({
  data,
  fullUrlPath,
  urlPath: initialPath,
  basePath,
  location,
  navLocation = null,
  store = "shop",
}) => {
  const dispatch = useAppDispatch();
  const { tokenData } = useAppSelector(getActiveUser);
  const { loggedInUser } = useAppSelector((state) =>
    getActiveUser(state, getLoggedinUser()),
  );

  const initializedPath = useMemo(() => {
    if (initialPath === "market-place") {
      return {
        storePath: basePath,
        basePath,
        fullPath: fullUrlPath || initialPath || "",
      };
    }

    const normalizedStorePath = location
      ? `${basePath.replace(/\/$/, "")}/${location.replace(/^\//, "")}`
      : basePath;

    return {
      storePath: normalizedStorePath,
      basePath,
      fullPath: fullUrlPath || initialPath || "",
    };
  }, [location, initialPath, basePath, fullUrlPath]);

  const storePath = useAppSelector((state) =>
    getStorePath(state, initializedPath.storePath),
  );

  useEffect(() => {
    dispatch(updateFullPath(initializedPath));
  }, []);

  const urlPath = useAppSelector((state) => getUrlPath(state, initialPath));
  const urlData = useAppSelector((state) => getUrlData(state, data?.urlData));
  const currentFullPath = useAppSelector((state) =>
    getFullPath(state, fullUrlPath || initialPath || ""),
  );

  const { exitPathOnLoad, navigateTo } = useHistoryManager(storePath, urlPath);

  const [searchTerm, setSearchTerm] = useState("");

  const accessPointId = useAppSelector((state) =>
    getAccessPointId(state, data?.accessPointId),
  );

  const organisation = useAppSelector((state) =>
    getOrganisation(state, data?.org?.organisation),
  );

  const accessPoint = useAppSelector((state) =>
    getAccessPoint(
      state,
      data?.accessPoints?.find((ap) => ap.accessPointId === data.accessPointId),
    ),
  );

  const accessPoints = useAppSelector((state) =>
    getAccessPoints(state, data?.accessPoints),
  );

  const cartSource = useAppSelector((state) =>
    getCartSource(state, store === "shop" ? "SHOP" : "MARKETPLACE"),
  );
  const isMarketplacePath = storePath?.toLowerCase().includes("market-place");

  const serverToken = data?.token || null;

  const categories = useAppSelector((state) =>
    getCategories(state, data?.categories),
  );
  const activeCategory = useAppSelector((state) => getActiveCategory(state));
  const activeCategoryData = useMemo(() => {
    if (!activeCategory) return null;

    return (
      categories?.find((category) => {
        return category?.productCategoryId === activeCategory;
      }) || null
    );
  }, [activeCategory, categories]);

  useEffect(() => {
    const nextTitle =
      navLocation === "marketplace"
        ? "CirclePlug Marketplace"
        : getStoreViewTitle({
            organisation,
            urlPath,
            urlData,
            activeCategory: activeCategoryData,
          });

    dispatch(setSiteTitle(nextTitle));

    return () => {
      dispatch(resetSiteTitle());
    };
  }, [
    dispatch,
    navLocation,
    organisation?.orgName,
    organisation?.name,
    organisation?.category,
    organisation?.country,
    urlPath,
    urlData?.name,
    urlData?.product?.name,
    activeCategoryData?.name,
  ]);

  useEffect(() => {
    const faviconUrl = getImageUrlWithResolution(
      organisation?.logoUrl,
      "r50x50",
    );

    setDocumentFavicon(faviconUrl || "/favicon.ico");

    return () => {
      setDocumentFavicon("/favicon.ico");
    };
  }, [organisation?.logoUrl]);

  const templateName = useMemo(
    () => resolveStoreTemplateName(data, organisation, navLocation),
    [data, organisation, navLocation],
  );
  const {
    StorePage,
    ProductPage,
    StoreModal,
    CartPage,
    AuthPage,
    ProfileContainer,
  } = storeTemplates[templateName];

  const [showProgress, setShowProgress] = useState(false);
  const [checkoutState, setCheckoutState] = useState("cartItems");
  const lastActiveCartRequestRef = useRef(null);

  const cart = useAppSelector((state) =>
    getCartByOrgAndAccessPoint(
      state,
      organisation?.orgId,
      accessPoint?.accessPointId,
      data.cart,
    ),
  );

  const {
    modal,
    modalData,
    modalState,
    modalBackButton,
    openModal,
    closeModal,
    setModalBackButton,
  } = useModalManager(storePath, showProgress, setShowProgress);

  const { isLoading: isCartLoading, mutate: mutateActiveCart } =
    useGetActiveCart({
      onSuccess: (response) => {
        const cart = response.data.collection;
        dispatch(updateCart(cart.profileId, cart.accessPointId, cart));
      },
      onError: (error) => {
        lastActiveCartRequestRef.current = null;
        console.error("Failed to fetch active cart:", error);
      },
    });

  useEffect(() => {
    dispatch(setCartSource(cartSource));
  }, [cartSource]);

  // Initial navigation guard
  useEffect(() => {
    exitPathOnLoad("auth", loggedInUser);
  }, [loggedInUser]);

  useEffect(() => {
    if (!accessPoint || accessPoint.accessPointId !== accessPointId) {
      const foundAp =
        accessPoints?.find((ap) => ap.accessPointId === accessPointId) ||
        accessPoint;
      if (foundAp) {
        dispatch(setAccessPoint(foundAp));
      }
    }
  }, [accessPointId, accessPoints, accessPoint]);

  // Sync cart from backend if token is present and not in Marketplace
  useEffect(() => {
    // Prevent fetching cart in Marketplace
    if (isMarketplacePath) {
      return;
    }

    const token = tokenData?.token;
    const orgId = organisation?.orgId;

    if (!token || !orgId || !accessPointId) {
      return;
    }

    const requestKey = [token, orgId, accessPointId, cartSource].join(":");
    if (lastActiveCartRequestRef.current === requestKey) {
      return;
    }

    lastActiveCartRequestRef.current = requestKey;
    mutateActiveCart({
      orgId,
      accessPointId,
      token,
      cartSource,
    });
  }, [
    tokenData?.token,
    organisation?.orgId,
    accessPointId,
    cartSource,
    isMarketplacePath,
    mutateActiveCart,
  ]);

  // Sync cart from props on mount
  useEffect(() => {
    if (data.cart) {
      const cart = data.cart;
      dispatch(updateCart(cart.profileId, cart.accessPointId, cart));
    }
  }, []);

  // Helpers
  const showCart = () => {
    const checkoutPath =
      storePath === "/" || storePath === "market-place"
        ? "checkout"
        : `${storePath.replace(/\/+$/, "")}/checkout`;

    if (modal) {
      closeModal();
    }

    if (navLocation === "marketplace") {
      navigateTo(
        "market-place/checkout",
        {
          urlData: {
            accessPoint,
            accessPoints,
            accessPointId,
            organisation,
          },
          onNavigate: (navigate) => {
            if (navigate) {
              dispatch(setAccessPointId(null));
              dispatch(setAccessPoint(null));
              dispatch(setAccessPoints([]));
              dispatch(setOrganisation(null));
              dispatch(setActiveCategory(null));
              dispatch(setCategories(null));
            }
          },
        },
        false,
      );
      return;
    }

    navigateTo(checkoutPath, {}, false);
  };

  const showProduct = async (product, incomingStorePath = "") => {
    const path =
      incomingStorePath && navLocation === "marketplace"
        ? incomingStorePath
        : storePath;

    const productUrlPath = generateProductUrl(path, product);

    if (templateName === "conveti" || (navLocation === "marketplace" && !modal)) {
      if (modal) {
        closeModal("navigate");
      }

      await navigateTo(
        productUrlPath,
        {
          state: { resetScroll: true },
          urlData: {
            product,
            accessPoint,
            accessPoints,
            accessPointId,
            organisation,
            storePath,
          },
        },
        false,
      );

      requestAnimationFrame(() => {
        window.scrollTo(0, 0);
      });
    } else {
      openModal("product", product, productUrlPath);
    }
  };

  const activateBackButton = (show, onClick = null) => {
    setModalBackButton({ show, onClick });
  };

  const commonProps = useMemo(
    () => ({
      data,
      showCart,
      cart,
      accessPointId,
      storePath,
      searchTerm,
      setSearchTerm,
      modal,
      activateBackButton,
      urlData,
      urlPath,
      fullUrlPath: currentFullPath,
      organisation,
      accessPoint,
      categories,
      serverToken,
      accessPoints,
      navLocation,
    }),
    [
      data,
      cart,
      accessPointId,
      storePath,
      searchTerm,
      urlData,
      urlPath,
      currentFullPath,
      organisation,
      accessPoint,
      categories,
      serverToken,
      accessPoints,
      navLocation,
    ],
  );

  // View router
  const renderView = () => {
    switch (urlPath) {
      case "home":
      case "category":
        return <StorePage {...commonProps} showProduct={showProduct} />;
      case "product":
        return <ProductPage {...commonProps} />;
      case "auth":
        return <AuthPage {...commonProps} />;
      case "checkout":
        if (storePath === "market-place") {
          return (
            <MarketplaceCartPage
              {...commonProps}
              showProgress={showProgress}
              showProduct={showProduct}
              setShowProgress={setShowProgress}
              checkoutState={checkoutState}
              setCheckoutState={setCheckoutState}
              closeModal={closeModal}
            />
          );
        }
        if (storePath !== "market-place") {
          return (
            <CartPage
              {...commonProps}
              showProgress={showProgress}
              showProduct={showProduct}
              setShowProgress={setShowProgress}
              checkoutState={checkoutState}
              setCheckoutState={setCheckoutState}
            />
          );
        }
      case "profile":
        return <ProfileContainer {...commonProps} />;
      case "market-place":
        return <MarketPlace {...commonProps} />;
      case "stores":
        return <MarketPlaceStore {...commonProps} />;
      default:
        return <NotFound />;
    }
  };

  return (
    <>
      {renderView()}
      {modal && (
        <ModalContainer
          closeModal={closeModal}
          modalBackButton={modalBackButton}
        >
          <StoreModal
            closeModal={closeModal}
            showCart={showCart}
            showProduct={showProduct}
            activateBackButton={activateBackButton}
            cart={cart}
            storePath={storePath}
            modalData={modalData}
            modalState={modalState}
            accessPoint={accessPoint}
            showProgress={showProgress}
            setShowProgress={setShowProgress}
            checkoutState={checkoutState}
            setCheckoutState={setCheckoutState}
            organisation={organisation}
          />
        </ModalContainer>
      )}
    </>
  );
};

export default Container;
